// =====================================================================
// src/db/intl-acca-types.ts
// =====================================================================
// TypeScript types for the International Acca module.
// Shapes here mirror the shipped Supabase migrations BIT-FOR-BIT:
//   - 20260517150000_intl_acca_a1_tournaments.sql
//   - 20260517160000_intl_acca_a2_slip_schema.sql
//   - 20260517170000_intl_acca_a4_pass_purchase.sql
//   - 20260517180000_intl_acca_b1_clv_audit.sql
//
// IMPORTANT — read before changing:
//   * `closing_price`, `clv_pp`, `clv_score_final`, `result`, `pinnacle_*`
//     are NULLABLE until a REAL Pinnacle close exists for the leg's match.
//     Every UI must render the null path (the "honest-empty" state) —
//     never fabricate a CLV value the engine has not produced.
//   * Selection-time fields (strikza_price, market_price_at_selection,
//     reasoning_note, selection, match_id) are write-once at the DB.
//     Treat them as immutable in UI logic.
//   * `reasoning_note` is NOT NULL and non-blank at the DB. The UI must
//     never hide it (PRD §12 hard constraint).
//
// Place in repo at: src/db/intl-acca-types.ts

// ────────────────────────────────────────────────────────────────────
// Tournaments — A1
// ────────────────────────────────────────────────────────────────────
export type TournamentRegion =
  | "GLOBAL"
  | "CAF"
  | "UEFA"
  | "CONMEBOL"
  | "AFC"
  | "CONCACAF";

export type TournamentStatus =
  | "PRE_TOURNAMENT"
  | "ACTIVE"
  | "POST_TOURNAMENT"
  | "ARCHIVED";

export type TournamentPriority = "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";

export interface IntlAccaTournament {
  id: string;
  name: string;
  region: TournamentRegion;
  start_date: string;          // ISO date
  end_date: string;            // ISO date
  status: TournamentStatus;
  pass_price_usd: number | null;
  pass_active: boolean;
  priority: TournamentPriority | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// ────────────────────────────────────────────────────────────────────
// Slips — A2
// ────────────────────────────────────────────────────────────────────
export type SlipType = "FOCUS" | "VALUE" | "POWER";
export type SlipStatus = "ACTIVE" | "WON" | "LOST" | "VOID";
export type ResultStatus = "WON" | "LOST" | "VOID";
export type CLVConfidence = "HIGH" | "MEDIUM" | "SPECULATIVE";

export interface IntlAccaSlip {
  id: string;
  tournament_id: string;
  slip_type: SlipType;
  status: SlipStatus;
  total_odds: number | null;
  /** Mean of settled leg clv_pp values. NULL until any leg has a Pinnacle close. */
  clv_score_final: number | null;
  generated_at: string;
  created_at: string;
}

export interface IntlAccaSlipLeg {
  id: string;
  slip_id: string;
  /** Stable match key — Match FK lands in a later C-phase slice. */
  match_id: string;
  selection: string;
  /** Model fair odds at selection time. */
  strikza_price: number;
  /** Best/Stake price at signal time — edge baseline. */
  market_price_at_selection: number;
  /** Pinnacle no-vig close at kickoff (CLV TRUTH). NULL until kickoff. */
  closing_price: number | null;
  implied_edge: number | null;
  clv_confidence: CLVConfidence;
  /** NOT NULL at the DB — never hide or blank this in UI (PRD §12). */
  reasoning_note: string;
  /** (mkt/close − 1) × 100 vs Pinnacle. NULL until closing_price exists. */
  clv_pp: number | null;
  result: ResultStatus | null;
  created_at: string;
}

// A slip with its legs joined — the shape every slip-detail surface consumes.
export interface IntlAccaSlipWithLegs extends IntlAccaSlip {
  legs: IntlAccaSlipLeg[];
}

// ────────────────────────────────────────────────────────────────────
// Pass purchases — A4
// ────────────────────────────────────────────────────────────────────
export type PassCurrency = "USD" | "GHS" | "NGN";

export interface IntlAccaPassPurchase {
  id: string;
  user_id: string;
  tournament_id: string;
  purchase_datetime: string;
  price_paid_usd: number;
  currency_paid: PassCurrency;
  paystack_reference: string | null;
  /** DB-forced to tournament end (PRD §12 — no extensions). */
  expires_at: string;
  is_complimentary: boolean;
  created_at: string;
}

// ────────────────────────────────────────────────────────────────────
// Tournament Debrief envelope — B1
// (returned by RPC public.intl_acca_tournament_debrief(p_tournament_id))
//
// Every aggregate field can be NULL when no legs/slips have settled.
// Per the migration, `executive_summary` values are computed from real
// settled legs only — they are "wired but empty" until Pinnacle closes
// flow in. UI MUST render the all-null path.
// ────────────────────────────────────────────────────────────────────
export interface DebriefExecutiveSummary {
  slips_total: number;
  slips_settled: number;
  /** 0–1 fraction. NULL when zero settled slips. */
  slip_win_rate: number | null;
  legs_total: number;
  legs_clv_audited: number;
  /** Mean leg CLV in percentage points. NULL when zero audited legs. */
  avg_leg_clv_pp: number | null;
  /** 0–1 fraction of audited legs with positive CLV. */
  clv_positive_rate: number | null;
  total_implied_edge: number | null;
}

export interface DebriefBestMarket {
  selection_pattern: string;
  n: number;
  avg_clv_pp: number | null;
}

export interface DebriefImprovementFlag {
  selection: string;
  n: number;
  avg_clv_pp: number | null;
}

export interface DebriefLegRow {
  match_id: string;
  selection: string;
  strikza_price: number;
  market_price_at_selection: number;
  closing_price: number | null;
  clv_pp: number | null;
  clv_confidence: CLVConfidence;
  result: ResultStatus | null;
}

export type DebriefEnvelope =
  | { ok: false; error: "tournament_not_found" }
  | {
      ok: true;
      tournament: string;
      executive_summary: DebriefExecutiveSummary;
      best_markets: DebriefBestMarket[];
      leg_clv_table: DebriefLegRow[];
      improvement_flags: DebriefImprovementFlag[];
      model_accuracy_note: string;
      pdf_export: string;
      generated_at: string;
    };

// ────────────────────────────────────────────────────────────────────
// Pass record RPC return — A4
// ────────────────────────────────────────────────────────────────────
export type RecordPassResult =
  | { ok: true; status: "recorded"; pass_id: string }
  | { ok: true; status: "already_held"; note: string }
  | { ok: false; error: string };

// ────────────────────────────────────────────────────────────────────
// Tournament state transition RPC return — A1
// ────────────────────────────────────────────────────────────────────
export type SetTournamentStateResult =
  | { ok: true; id: string; from: TournamentStatus; to: TournamentStatus; pass_active: boolean | null }
  | { ok: false; error: string; from?: TournamentStatus; to?: TournamentStatus };

// ────────────────────────────────────────────────────────────────────
// CLV settle return — B1
// ────────────────────────────────────────────────────────────────────
export interface SettleClvResult {
  profile: "intl_acca_clv_audit";
  status: "success";
  legs_settled: number;
  slips_rescored: number;
  note: string;
  ran_at: string;
}

// ────────────────────────────────────────────────────────────────────
// UI-only helpers — derived, not stored
// ────────────────────────────────────────────────────────────────────

/** Settlement readiness of a leg — drives which UI cells are populated. */
export type LegSettlementStage =
  | "unsettled"      // pre-kickoff: closing_price null
  | "audited"        // closing_price + clv_pp present, result not yet
  | "resolved";      // result present (WON / LOST / VOID)

export function legStage(leg: IntlAccaSlipLeg): LegSettlementStage {
  if (leg.result !== null) return "resolved";
  if (leg.closing_price !== null && leg.clv_pp !== null) return "audited";
  return "unsettled";
}

/** Format a CLV percentage-point value: +6.4pp / −2.1pp / "—" for null. */
export function formatClvPp(clv_pp: number | null): string {
  if (clv_pp === null || !Number.isFinite(clv_pp)) return "—";
  const sign = clv_pp >= 0 ? "+" : "−";
  return `${sign}${Math.abs(clv_pp).toFixed(1)}pp`;
}

/** Format a decimal odds value: "1.85" / "—" for null. */
export function formatOdds(odds: number | null): string {
  if (odds === null || !Number.isFinite(odds) || odds <= 1) return "—";
  return odds.toFixed(2);
}
